import { useEffect, useRef } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useVoiceAgentStore, IncomingCallContext } from '@stores/voiceAgentStore';
import { useAuthStore } from '@stores/authStore';
import voiceService from '@services/voice.service';
import { useWebSocket } from '@/hooks/useWebSocket';

/** How often an online agent's console tells the server it is still there. */
const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * AgentPhone — persistent component that lives in the authenticated layout.
 * Initialises a Twilio Device, registers it with Twilio, and listens for
 * incoming calls. Must be mounted for agents to receive calls. When the server
 * says calls ring over the media bridge instead (no token), it only marks the
 * browser ready; BridgePhone takes those calls.
 *
 * The agent's "online" choice is a preference kept in the store. The server is
 * told the agent is online only while the softphone is actually registered, so
 * a call is never offered to a browser that cannot ring.
 */
export function AgentPhone() {
  const user = useAuthStore((s) => s.user);
  const { setDevice, setDeviceReady, setIncomingCall, setActiveCall, reset } = useVoiceAgentStore();
  const deviceRef = useRef<Device | null>(null);

  const isOnline = useVoiceAgentStore((s) => s.isOnline);
  const isDeviceReady = useVoiceAgentStore((s) => s.isDeviceReady);
  const { isConnected: isLive } = useWebSocket();
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    const userId = user.id;
    const userName = user.name;

    async function initDevice() {
      try {
        let setup;
        try {
          setup = await voiceService.getAgentToken(userId, userName);
        } catch (error: any) {
          // The server says this browser cannot take calls (e.g. voice is not
          // set up for this account). Shown on the status control.
          if (!cancelled) {
            useVoiceAgentStore
              .getState()
              .setPhoneError(error?.response?.data?.message || 'Voice calling is not available for this account');
          }
          return;
        }
        if (cancelled) return;
        const { token, mediaUrl } = setup;
        useVoiceAgentStore.getState().setPhoneError(null);
        useVoiceAgentStore.getState().setMediaUrl(mediaUrl ?? null);

        // No softphone token: this tenant's calls ring the browser over the
        // platform's media bridge (BridgePhone), which needs nothing set up
        // here. The browser can take calls as it is, so honour the saved
        // "online" choice now rather than waiting on a softphone that never
        // comes.
        if (!token) {
          setDeviceReady(true);
          if (isOnlineRef.current) {
            voiceService.setAgentOnline(userId, userName).catch(() => {});
          }
          return;
        }

        const device = new Device(token, { logLevel: 'warn' });

        device.on('registered', () => {
          if (cancelled) return;
          setDeviceReady(true);
          // The softphone can ring now: honour the saved "online" choice.
          if (isOnlineRef.current) {
            voiceService.setAgentOnline(userId, userName).catch(() => {});
          }
        });

        device.on('unregistered', () => {
          setDeviceReady(false);
          // Can't ring any more, so stop being offered calls. The preference
          // stays, and re-registering brings the agent back online.
          if (isOnlineRef.current) {
            voiceService.setAgentOffline(userId).catch(() => {});
          }
        });

        device.on('error', (error) => {
          console.error('[AgentPhone] Device error:', error.message);
          setDeviceReady(false);
        });

        // Refresh before every expiry, not just the first.
        device.on('tokenWillExpire', async () => {
          try {
            const { token: newToken } = await voiceService.getAgentToken(userId, userName);
            if (newToken) device.updateToken(newToken);
          } catch {
            // Non-fatal: the device reports an error and re-initialises on reload.
          }
        });

        device.on('incoming', (call) => {
          // Extract call context from customParameters sent by the server
          const params = call.customParameters;
          const context: IncomingCallContext = {
            customerPhone: params.get('customerPhone') ?? 'Unknown',
            handoffReason: params.get('handoffReason') ?? undefined,
            ticketId: params.get('ticketId') ?? undefined,
            sessionId: params.get('sessionId') ?? undefined,
            transferredBy: params.get('transferredBy') || undefined,
            transferNote: params.get('transferNote') || undefined,
            transferReturned: params.get('transferReturned') === 'true' || undefined,
          };
          // The call context carries customer details (phone, conversation
          // summary), so none of it is written to the browser console.
          const aiSummaryRaw = params.get('aiSummary');
          if (aiSummaryRaw) {
            try {
              context.aiSummary = JSON.parse(aiSummaryRaw);
            } catch {
              console.warn('[AgentPhone] Incoming call aiSummary could not be parsed');
            }
          }

          setIncomingCall(call, context);

          call.on('cancel', () => setIncomingCall(null));
          call.on('disconnect', () => {
            setActiveCall(null);
            reset();
          });
        });

        await device.register();
        deviceRef.current = device;
        setDevice(device);
      } catch (error) {
        console.error('[AgentPhone] Failed to initialise device:', error);
      }
    }

    initDevice();

    // Closing or reloading the tab: stop being offered calls. A reload
    // registers again and comes back online by itself.
    const onPageHide = () => {
      if (isOnlineRef.current) voiceService.setAgentOfflineOnUnload();
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      // Logging out (or this layout unmounting) also takes the agent offline.
      if (isOnlineRef.current) {
        voiceService.setAgentOffline(userId).catch(() => {});
      }
      deviceRef.current?.destroy();
      deviceRef.current = null;
      setDevice(null);
      setDeviceReady(false);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // While online with a working softphone, keep the server's record fresh.
  // If the server no longer has the agent as online (its entry expired),
  // register again.
  //
  // Only while live updates are connected: a call is offered over them, so a
  // browser that cannot hear the offer must not look reachable. With the
  // check-ins stopped the server stops offering this agent calls within a few
  // minutes, and they resume on reconnect.
  useEffect(() => {
    if (!user?.id || !isOnline || !isDeviceReady || !isLive) return;
    const userId = user.id;
    const userName = user.name;

    const beat = async () => {
      try {
        const { online } = await voiceService.agentHeartbeat();
        if (!online && isOnlineRef.current) {
          await voiceService.setAgentOnline(userId, userName);
        }
      } catch {
        // Transient; the next beat retries.
      }
    };

    void beat();
    const interval = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user?.id, isOnline, isDeviceReady, isLive]); // eslint-disable-line react-hooks/exhaustive-deps

  // This component renders nothing — it is a background singleton
  return null;
}
